import { CircleHalfIcon, DotsThreeIcon, SignOutIcon, UserCircleIcon } from "@phosphor-icons/react";
import { Suspense, useEffect, useRef, useState } from "react";
import { matchPath, NavLink, Outlet, useLocation } from "react-router";

import {
  DETAIL_ROUTES,
  GROUP_LABELS,
  GROUP_ORDER,
  phonePrimaryRoutes,
  routesVisibleTo,
  SETTINGS_TABS,
  WORKSPACE_ROUTES_BY_PATH,
  type WorkspaceRoute,
} from "../../app/route-manifest";
import { resolveWorkspaceEntry } from "../../app/workspace-entry";
import { resolveRouteAccess } from "../../auth/route-access";
import { ROLE_LABELS, WEB_WORKSPACE_ROLES } from "../../auth/roles";
import { sessionRole, useSession, type WorkspaceSession } from "../../auth/session";
import { useTheme, type ThemePreference } from "../../app/theme-context";
import { useToast } from "../feedback/toast-context";
import { ConnectionStatusBanner } from "../feedback/ConnectionStatusBanner";
import { StatusBanner } from "../feedback/StatusBanner";
import { LegalAcceptanceScreen } from "../../app/lazy-screens";
import { BusinessLogo } from "./BusinessLogo";

/** Public on purpose: readable before signing in (P1-023). */
const LEGAL_FOOTER_LINKS = [
  { label: "Terms of Service", path: "/legal/terms" },
  { label: "Privacy and Data Processing Notice", path: "/legal/privacy" },
] as const;

const THEME_ORDER: readonly ThemePreference[] = ["system", "light", "dark"];
const THEME_LABELS: Record<ThemePreference, string> = {
  dark: "Dark",
  light: "Light",
  system: "Match system",
};

/**
 * The one name a refused screen is ever given. It is deliberately the same
 * string for every refusal, so comparing two refusals tells a viewer nothing
 * about which screens exist behind them.
 */
export const UNAVAILABLE_SCREEN_TITLE = "Not available";

/**
 * A pathname reduced to the form the route table is keyed by, so `/staff/` and
 * `/staff` cannot resolve to different titles for the same rendered screen.
 */
function canonicalPath(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
}

/**
 * The screen title used for the document title and the live announcement.
 *
 * It must describe what was actually drawn, never what was asked for. Every
 * branch here is derived from the same decision the renderer used:
 * `resolveRouteAccess` for a guarded route and `resolveWorkspaceEntry` for the
 * entry point. Deriving either of them independently is what previously let the
 * tab title and the screen-reader announcement name a screen the gate had just
 * refused to draw.
 *
 * So: a refused screen is `UNAVAILABLE_SCREEN_TITLE` and nothing else. No
 * route name, no reason, no hint that the address means anything at all.
 */
/** Screens anyone can open, before any session (P1-013); each heading names it. */
const PUBLIC_TITLES: Partial<Record<string, string>> = {
  "/assisted-recovery": "Recover your account",
  "/invitations/accept": "Accept your invitation",
  "/legal/privacy": "Privacy and Data Processing Notice",
  "/legal/terms": "Terms of Service",
  "/password-reset": "Reset your password",
  "/replace-authenticator": "Replace your authenticator",
  "/sign-in": "Sign in",
};

/** What an Admin or Manager sees until they have accepted the current terms (P1-023). */
const LEGAL_GATE_TITLE = "Accept the terms to continue";

/**
 * True while a signed-in person still has a document to accept. The shell then
 * draws only the acceptance screen (and sign-out), at every address except the
 * documents themselves, which are public. A drawing decision: the API records
 * the acceptance, and the person cannot skip it by typing an address.
 */
function legalGateApplies(pathname: string, session: WorkspaceSession): boolean {
  return (
    session.kind === "signed-in" &&
    (session.legalOutstanding?.length ?? 0) > 0 &&
    !canonicalPath(pathname).startsWith("/legal/")
  );
}

function screenTitleFor(pathname: string, session: WorkspaceSession): string {
  const path = canonicalPath(pathname);

  // Before the public titles: a person with terms to accept sees only that
  // screen, even at /sign-in, which a signed-in person is sent away from.
  if (legalGateApplies(pathname, session)) {
    return LEGAL_GATE_TITLE;
  }
  const publicTitle = PUBLIC_TITLES[path];
  if (publicTitle) {
    return publicTitle;
  }
  // Before the API has answered, nothing has been decided, so nothing is named.
  if (session.kind === "checking" || session.kind === "unavailable") {
    return "Loading";
  }

  // A detail screen is gated exactly as its parent is, and named for itself.
  const detail = DETAIL_ROUTES.find((d) => matchPath({ end: true, path: d.path }, path));
  const route = WORKSPACE_ROUTES_BY_PATH.get(detail?.parent ?? path) ?? SETTINGS_TABS.get(path);
  if (route) {
    return resolveRouteAccess({ allowedRoles: route.allowedRoles, permission: route.drawnWith, session }).granted
      ? (detail?.title ?? route.title)
      : UNAVAILABLE_SCREEN_TITLE;
  }

  if (path === "/account") {
    return resolveRouteAccess({ allowedRoles: WEB_WORKSPACE_ROLES, session }).granted
      ? "Account and security"
      : UNAVAILABLE_SCREEN_TITLE;
  }

  if (path === "/") {
    // The entry point refuses anyone who has no workspace. Calling that
    // "Workspace" would both contradict the refusal on screen and tell the
    // viewer a workspace exists for them, so it is named like any other
    // refusal.
    return resolveWorkspaceEntry(session).kind === "denied"
      ? UNAVAILABLE_SCREEN_TITLE
      : "Workspace";
  }

  return "Page not found";
}

/*
  The transitions name their properties instead of using `transition-colors`,
  which also transitions `outline-color`. When it does, the focus ring is
  painted in `currentColor` rather than the focus token, so the indicator
  quietly stops being the designed colour. `Button` avoids it the same way.
*/
const FOCUS_RING =
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus";

/** The sidebar and the More panel: every screen the role can open, by job. */
function NavigationList({
  onNavigate,
  routes,
}: {
  onNavigate?: () => void;
  routes: readonly WorkspaceRoute[];
}) {
  if (routes.length === 0) {
    return (
      <p className="px-3 py-2 text-sm leading-6 text-text-muted">
        No workspace screens are listed for this account.
      </p>
    );
  }

  return (
    <div className="space-y-5">
      {GROUP_ORDER.map((group) => {
        const groupRoutes = routes.filter((route) => route.group === group);
        if (groupRoutes.length === 0) {
          return null;
        }
        return (
          <div key={group}>
            {/*
              The group label is exposed through the list's accessible name
              rather than as a heading, so the navigation cannot introduce an
              `h2` ahead of the screen's own `h1`.
            */}
            <p aria-hidden="true" className="px-4 text-xs font-bold tracking-wider text-text-muted uppercase">
              {GROUP_LABELS[group]}
            </p>
            <ul aria-label={GROUP_LABELS[group]} className="mt-1.5 space-y-1">
              {groupRoutes.map(({ icon: Icon, navLabel, path }) => (
                <li key={path}>
                  <NavLink
                    className={`flex min-h-12 items-center gap-3 rounded-full px-4 text-[0.9375rem] font-semibold text-text transition-[background-color,color] duration-150 hover:bg-surface-raised aria-[current=page]:bg-accent aria-[current=page]:font-bold aria-[current=page]:text-on-accent ${FOCUS_RING}`}
                    onClick={onNavigate}
                    to={path}
                  >
                    <Icon aria-hidden="true" className="size-5 shrink-0" />
                    <span className="min-w-0">{navLabel}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

// At 320 px five slots are about 62 px each. At 12 px "Exceptions" (hidden
// until its slice, P5-013) is 60.5 px wide, too close to trust across font renderers, so labels are 11 px below
// 360 px (about 6 px to spare) and 12 px above. e2e/shell.spec.ts measures it.
const BOTTOM_ITEM = `group flex min-h-16 w-full flex-col items-center justify-center gap-0.5 rounded-control text-[0.6875rem] font-semibold tracking-tight text-text-muted min-[360px]:text-xs ${FOCUS_RING}`;
const BOTTOM_PILL =
  "grid h-8 w-14 place-items-center rounded-full transition-[background-color,color] duration-150";

/**
 * The management shell, after the D & S manager portal (ADR 0027, P3-011
 * slice 7): a sidebar grouped by job on wide screens, and on phones a bottom
 * bar with the role's four main screens and More for the rest.
 *
 * Accessibility decisions that live here:
 *
 * - A skip link is the first focusable element on every screen.
 * - `banner`, `main` and `contentinfo` landmarks exist once each. The sidebar,
 *   the bottom bar and the More panel are distinctly named navigations; CSS
 *   shows the sidebar or the bottom bar, never both.
 * - Changing route moves focus to `main` and announces the new screen in a
 *   polite live region, because the browser's own page-change behaviour does
 *   not happen in a single-page application.
 * - Every control is at least 48 by 48 CSS pixels (`min-h-12` / `size-12`).
 * - Navigation state is carried by `aria-current`, weight and background
 *   together, never by colour alone.
 */
export function AppShell() {
  const location = useLocation();
  const { preference, setPreference } = useTheme();
  const { session, signOut } = useSession();
  const { showToast } = useToast();
  const [signOutFailed, setSignOutFailed] = useState(false);

  const mainRef = useRef<HTMLElement>(null);
  const moreRef = useRef<HTMLButtonElement>(null);
  const previousPath = useRef(location.pathname);
  const [menuOpen, setMenuOpen] = useState(false);

  const role = sessionRole(session);
  const branding = session.kind === "signed-in" ? session.branding : null;
  const businessName = branding?.business_name ?? null;
  // Until the API answers, neither the BurgePOS mark nor a name is drawn, so
  // a business's own never replaces it on screen.
  const checking = session.kind === "checking";
  // Until the terms are accepted nothing else is offered: no navigation, no account link.
  const gated = legalGateApplies(location.pathname, session);
  const visibleRoutes = gated ? [] : routesVisibleTo(role, session.kind === "signed-in" ? session.shop?.permissions : undefined);
  const primaryRoutes = gated ? [] : phonePrimaryRoutes(role);
  const hasMore = visibleRoutes.length > primaryRoutes.length;
  const onMoreScreen =
    hasMore && !primaryRoutes.some((route) => route.path === canonicalPath(location.pathname)) &&
    visibleRoutes.some((route) => route.path === canonicalPath(location.pathname));
  const screenTitle = screenTitleFor(location.pathname, session);

  useEffect(() => {
    document.title = `${screenTitle} · BurgePOS`;
  }, [screenTitle]);

  useEffect(() => {
    if (previousPath.current === location.pathname) {
      return;
    }
    previousPath.current = location.pathname;
    setMenuOpen(false);
    mainRef.current?.focus();
  }, [location.pathname]);

  // The gate appearing (sign-in) or lifting (after Accept) replaces the whole
  // screen without a route change, so focus is moved here as well.
  const wasGated = useRef(false);
  useEffect(() => {
    if (wasGated.current !== gated) {
      wasGated.current = gated;
      mainRef.current?.focus();
    }
  }, [gated]);

  const endSession = async () => {
    try {
      await signOut();
      setSignOutFailed(false);
      showToast("Signed out.");
    } catch {
      // Still signed in: say so where it stays visible, not in a toast.
      setSignOutFailed(true);
    }
  };

  const cycleTheme = () => {
    const index = THEME_ORDER.indexOf(preference);
    const next = THEME_ORDER[(index + 1) % THEME_ORDER.length] ?? "system";
    setPreference(next);
    showToast(`Appearance set to ${THEME_LABELS[next].toLowerCase()}.`);
  };

  // Escape closes More wherever focus is: right after opening, focus is
  // still on the More button, outside the sheet.
  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        moreRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [menuOpen]);

  return (
    <div className="flex min-h-[100dvh] overflow-x-clip bg-canvas text-text">
      <a
        className={`fixed left-4 top-4 z-skip inline-flex min-h-12 -translate-y-24 items-center rounded-control bg-accent px-4 font-semibold text-on-accent focus:translate-y-0 ${FOCUS_RING}`}
        href="#main-content"
      >
        Skip to content
      </a>

      {gated ? null : (
        <nav
          aria-label="Primary navigation"
          className="sticky top-0 hidden h-[100dvh] w-68 shrink-0 flex-col overflow-y-auto bg-canvas px-4 py-6 lg:flex print:hidden"
        >
          <div className="flex items-center gap-3 px-2 pb-7">
            <BusinessLogo branding={branding} pending={checking} />
            {checking ? null : (
              <span className="min-w-0">
                <span className="block truncate text-[0.9375rem] font-extrabold">{businessName ?? "BurgePOS"}</span>
                <span className="block text-xs text-text-muted">{businessName ? "BurgePOS Management" : "Management"}</span>
              </span>
            )}
          </div>
          <NavigationList routes={visibleRoutes} />
        </nav>
      )}

      <div className={`flex min-w-0 flex-1 flex-col lg:pb-0 ${primaryRoutes.length > 0 ? "pb-20" : ""}`}>
        <header className="sticky top-0 z-header bg-canvas/95 backdrop-blur-sm print:hidden">
          <div className="mx-auto flex min-h-18 max-w-shell items-center justify-between gap-2 px-4 py-3 sm:px-6 lg:justify-end lg:px-10">
            <NavLink
              className={`flex min-h-12 items-center gap-3 rounded-control lg:hidden ${FOCUS_RING}`}
              to="/"
            >
              <BusinessLogo branding={branding} pending={checking} small />
              {checking ? null : (
                <span className="max-w-40 truncate text-base font-extrabold tracking-tight sm:max-w-none">{businessName ?? "BurgePOS"}</span>
              )}
            </NavLink>

            {role && !gated ? (
              <NavLink
                aria-label={`Account and security, signed in as ${ROLE_LABELS[role]}`}
                className={`inline-flex min-h-12 min-w-12 items-center justify-center gap-2 rounded-full bg-accent-soft px-4 text-sm font-bold text-accent-strong ${FOCUS_RING}`}
                to="/account"
              >
                <UserCircleIcon aria-hidden="true" className="size-5" />
                <span className="hidden sm:inline">{ROLE_LABELS[role]}</span>
              </NavLink>
            ) : null}
            <button
              aria-label={`Appearance: ${THEME_LABELS[preference]}. Change appearance`}
              // Icon-only on phones, so it also needs the width of a target.
              className={`inline-flex min-h-12 min-w-12 items-center justify-center gap-2 rounded-full bg-surface-raised px-4 text-sm font-semibold text-text-muted shadow-panel hover:text-text ${FOCUS_RING}`}
              onClick={cycleTheme}
              type="button"
            >
              <CircleHalfIcon aria-hidden="true" className="size-5" />
              <span className="hidden sm:inline">{THEME_LABELS[preference]}</span>
            </button>
            {session.kind === "signed-in" ? (
              <button
                aria-label="Sign out"
                className={`inline-flex min-h-12 min-w-12 items-center justify-center gap-2 rounded-full bg-surface-raised px-4 text-sm font-semibold text-text-muted shadow-panel hover:text-text ${FOCUS_RING}`}
                onClick={() => void endSession()}
                type="button"
              >
                <SignOutIcon aria-hidden="true" className="size-5" />
                <span className="hidden sm:inline">Sign out</span>
              </button>
            ) : null}
          </div>
        </header>

        <main
          // scroll-mt clears the sticky header when focus moves here on a
          // route change, so the first thing on the screen is never hidden.
          className="mx-auto w-full min-w-0 max-w-shell flex-1 scroll-mt-24 px-4 py-7 focus:outline-none sm:px-6 lg:px-10 lg:py-9 print:p-0"
          id="main-content"
          ref={mainRef}
          tabIndex={-1}
        >
          <ConnectionStatusBanner className="mb-8 print:hidden" />
          {signOutFailed ? (
            <StatusBanner
              className="mb-8"
              description="You are still signed in. Check this device's connection, then sign out again."
              title="Sign-out did not finish"
              tone="danger"
            />
          ) : null}
          <Suspense fallback={<p className="text-sm text-text-muted" role="status">Loading…</p>}>
            {gated ? <LegalAcceptanceScreen /> : <Outlet />}
          </Suspense>
        </main>

        <footer className="px-4 py-6 text-sm text-text-muted sm:px-6 lg:px-10 print:hidden">
          <div className="mx-auto max-w-shell">
            BurgePOS management workspace. Figures come from what each shop till has synced.
            <div className="mt-2 flex flex-wrap gap-x-6">
              {LEGAL_FOOTER_LINKS.map(({ label, path }) => (
                <NavLink
                  className={`inline-flex min-h-12 items-center font-semibold text-text underline underline-offset-4 ${FOCUS_RING}`}
                  key={path}
                  to={path}
                >
                  {label}
                </NavLink>
              ))}
            </div>
          </div>
        </footer>
      </div>

      {primaryRoutes.length > 0 ? (
        <nav
          aria-label="Main sections"
          className="fixed inset-x-0 bottom-0 z-header border-t border-border bg-surface-raised pb-[env(safe-area-inset-bottom)] lg:hidden print:hidden"
        >
          <ul className="mx-auto flex max-w-xl px-1">
            {primaryRoutes.map(({ icon: Icon, path, shortLabel }) => (
              <li className="min-w-0 flex-1" key={path}>
                <NavLink
                  className={`${BOTTOM_ITEM} aria-[current=page]:font-bold aria-[current=page]:text-accent-strong`}
                  to={path}
                >
                  <span className={`${BOTTOM_PILL} group-aria-[current=page]:bg-accent-soft`}>
                    <Icon aria-hidden="true" className="size-5" />
                  </span>
                  <span className="max-w-full truncate">{shortLabel}</span>
                </NavLink>
              </li>
            ))}
            {hasMore ? (
              <li className="min-w-0 flex-1">
                <button
                  aria-controls="workspace-navigation"
                  aria-expanded={menuOpen}
                  className={`${BOTTOM_ITEM} ${onMoreScreen || menuOpen ? "font-bold text-accent-strong" : ""}`}
                  onClick={() => setMenuOpen((open) => !open)}
                  ref={moreRef}
                  type="button"
                >
                  <span className={`${BOTTOM_PILL} ${onMoreScreen || menuOpen ? "bg-accent-soft" : ""}`}>
                    <DotsThreeIcon aria-hidden="true" className="size-6" weight="bold" />
                  </span>
                  More
                </button>
              </li>
            ) : null}
          </ul>
        </nav>
      ) : null}

      {/*
        Always in the document while More exists, so `aria-controls` resolves,
        and hidden with the `hidden` attribute so assistive technology and the
        tab order agree with what is painted.
      */}
      {hasMore ? (
        <nav
          aria-label="Workspace navigation"
          className="fixed inset-x-0 bottom-16 z-overlay max-h-[70dvh] overflow-y-auto rounded-t-dialog border-t border-border bg-surface-raised px-4 py-5 shadow-dialog lg:hidden"
          hidden={!menuOpen}
          id="workspace-navigation"
        >
          <NavigationList onNavigate={() => setMenuOpen(false)} routes={visibleRoutes} />
        </nav>
      ) : null}

      {/*
        Announces the screen change for a screen reader. Focus alone moves the
        reading position but does not reliably say where the person has arrived.
      */}
      <p aria-live="polite" className="sr-only">
        {screenTitle}
      </p>
    </div>
  );
}
